<template>
    <div class="space-y-6">
        <participant-detail-section>
            {{ $t('purchaseContract.participants') }}
            <span class="text-sm font-normal">
                {{
                    $t('purchaseContract.selectExistingHouseholdMemberOrAddNew')
                }}
            </span>
        </participant-detail-section>

        <div v-if="isLoading">
            <skeleton />
        </div>

        <template v-else>
            <template
                v-for="(
                    enrollmentInput, enrollmentInputIndex
                ) in enrollmentInputs"
                :key="enrollmentInput.enrollmentType.id"
            >
                <enrollment-slot
                    v-for="(
                        enrollment, enrollmentIndex
                    ) in enrollmentInput.enrollments"
                    :key="`${enrollmentInput.enrollmentType.id} ${enrollmentIndex}`"
                    :show-errors="showErrors"
                    :enrollment-type="enrollmentInput.enrollmentType"
                    :enrollment="enrollment"
                    :sequence="enrollmentIndex + 1"
                    :participant-options="participantOptions"
                    :enrollment-constraints="enrollmentConstraints"
                    :is-guest="isGuest"
                    :plan-type="contractData.planType"
                    @update:enrollment="
                        handleUpdateEnrollment(
                            enrollmentInputIndex,
                            enrollmentIndex,
                            $event
                        )
                    "
                />
            </template>
        </template>
    </div>
</template>

<script lang="ts">
import { graphql } from '~/resources/graphql'

export const EnrollmentCustomerPickerContractFragment = graphql(`
    fragment EnrollmentCustomerPickerContractFragment on PlanContract {
        planType
        enrollmentSpecifications {
            enrollments {
                customer {
                    id
                    commaName
                    firstName
                    middleName
                    lastName
                    dateOfBirth
                }
            }
        }
    }
`)
</script>

<script setup lang="ts">
import { computed } from 'vue'
import { useRpQuery } from '~/composables/graphql'
import { useQuery } from '@tanstack/vue-query'
import Skeleton from 'primevue/skeleton'
import ParticipantDetailSection from '~/components/PurchaseContract/ParticipantDetails/ParticipantDetailSection.vue'
import {
    type EnrollmentInputArray,
    type EnrollmentInputEnrollment,
    type EnrollmentInputExistingEnrollment,
    makeEmptyEnrollmentInput
} from '~/composables/enrollment-input'
import EnrollmentSlot from '~/components/PurchaseContract/CustomerPicker/EnrollmentSlot.vue'
import { type FragmentType, getFragmentData } from '~/resources/graphql'
import type { EnrollmentConstraints } from '~/components/PurchaseContract/ParticipantDetails/enrollment-constraint'

const props = defineProps<{
    enrollmentInputs: EnrollmentInputArray
    contract: FragmentType<typeof EnrollmentCustomerPickerContractFragment>
    showErrors: boolean
    isGuest: boolean
    enrollmentConstraints: EnrollmentConstraints
}>()

const emit = defineEmits<{
    (e: 'update:enrollmentInputs', payload: EnrollmentInputArray): void
}>()

const contractData = computed(() =>
    getFragmentData(EnrollmentCustomerPickerContractFragment, props.contract)
)

const query = useRpQuery()
// TODO: when there are more than 50 customers
// show birthday and/or populate the 'add new customer' fields upon selection?
const { data: profileCustomersData, isLoading } = useQuery({
    queryKey: ['ProfileCustomersQuery'],
    placeholderData: previousData => previousData,
    queryFn: () =>
        query(
            graphql(/* GraphQL */ `
                query ProfileCustomersQuery {
                    viewer {
                        primaryCustomer {
                            id
                            commaName
                            firstName
                            middleName
                            lastName
                            dateOfBirth
                        }
                        savedParticipants(first: 50) {
                            edges {
                                node {
                                    id
                                    commaName
                                    firstName
                                    middleName
                                    lastName
                                    dateOfBirth
                                }
                            }
                        }
                    }
                }
            `)
        )
})

const participantOptions = computed<
    (EnrollmentInputExistingEnrollment & { disabled: boolean })[]
>(() => {
    const participantOptions: EnrollmentInputExistingEnrollment[] = []

    contractData.value.enrollmentSpecifications.forEach(spec => {
        spec.enrollments.forEach(enrollment => {
            if (
                participantOptions.find(
                    option => option.id === enrollment.customer.id
                ) === undefined
            ) {
                participantOptions.push({
                    type: 'existing',
                    id: enrollment.customer.id,
                    commaName: enrollment.customer.commaName,
                    firstName: enrollment.customer.firstName,
                    middleName: enrollment.customer.middleName,
                    lastName: enrollment.customer.lastName,
                    dateOfBirth: enrollment.customer.dateOfBirth ?? ''
                })
            }
        })
    })

    if (
        profileCustomersData.value !== undefined &&
        profileCustomersData.value.viewer !== null
    ) {
        profileCustomersData.value.viewer.savedParticipants.edges.forEach(
            edge => {
                if (
                    participantOptions.find(
                        option => option.id === edge.node.id
                    ) === undefined
                ) {
                    participantOptions.push({
                        type: 'existing',
                        id: edge.node.id,
                        commaName: edge.node.commaName,
                        firstName: edge.node.firstName,
                        middleName: edge.node.middleName,
                        lastName: edge.node.lastName,
                        dateOfBirth: edge.node.dateOfBirth ?? ''
                    })
                }
            }
        )

        // If the primary customer has not already been added, add to front of list
        if (
            participantOptions.find(
                option =>
                    option.id ===
                    profileCustomersData.value.viewer?.primaryCustomer.id
            ) === undefined
        ) {
            participantOptions.unshift({
                type: 'existing',
                id: profileCustomersData.value.viewer.primaryCustomer.id,
                commaName:
                    profileCustomersData.value.viewer.primaryCustomer.commaName,
                firstName:
                    profileCustomersData.value.viewer.primaryCustomer.firstName,
                middleName:
                    profileCustomersData.value.viewer.primaryCustomer
                        .middleName,
                lastName:
                    profileCustomersData.value.viewer.primaryCustomer.lastName,
                dateOfBirth:
                    profileCustomersData.value.viewer.primaryCustomer
                        .dateOfBirth ?? ''
            })
        }
    }

    return participantOptions.map(customer => ({
        type: 'existing',
        id: customer.id,
        commaName: customer.commaName,
        firstName: customer.firstName,
        middleName: customer.middleName,
        lastName: customer.lastName,
        dateOfBirth: customer.dateOfBirth,
        disabled: selectedCustomers.value.has(customer.id)
    }))
})

// Currently selected customers
const selectedCustomers = computed(() => {
    const set = new Set<string>()
    props.enrollmentInputs.forEach(value => {
        value.enrollments.forEach(enrollment => {
            if (
                enrollment.data !== null &&
                enrollment.data.type === 'existing'
            ) {
                set.add(enrollment.data.id)
            }
        })
    })
    return set
})

function handleUpdateEnrollment(
    enrollmentInputIndex: number,
    enrollmentIndex: number,
    enrollment: EnrollmentInputEnrollment
) {
    const oldEnrollments =
        props.enrollmentInputs[enrollmentInputIndex].enrollments
    const enrollments = [
        ...oldEnrollments.slice(0, enrollmentIndex),
        enrollment,
        ...oldEnrollments.slice(enrollmentIndex + 1)
    ]

    emit('update:enrollmentInputs', [
        ...props.enrollmentInputs.slice(0, enrollmentInputIndex),
        {
            ...props.enrollmentInputs[enrollmentInputIndex],
            enrollments
        },
        ...props.enrollmentInputs.slice(enrollmentInputIndex + 1)
    ])
}
</script>
